import React from "react";
import { Button, Col, Form, FormGroup, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "common/api";
import * as formik from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import usePageTitle from "common/usePageTitle";
import "./MaturaCroatianEssayApplicationPage.css";

const CROATIAN_ESSAY_ID = 277;

function MaturaCroatianEssayApplicationPage() {
  const navigate = useNavigate();
  const { Formik } = formik;

  usePageTitle("Prijava mature - esej");

  const schema = yup.object({
    firstName: yup.string().required("Potrebno je unijeti ime učenika"),
    lastName: yup.string().required("Potrebno je unijeti prezime učenika"),
    school: yup.string().required("Potrebno je unijeti naziv škole"),
    phoneNumber: yup
      .string()
      .required("Potrebno je unijeti kontakt broj učenika"),
    parentPhoneNumber: yup
      .string()
      .required("Potrebno je unijeti kontakt broj roditelja"),
    email: yup
      .string()
      .email("Unesite ispravnu email adresu")
      .required("Potrebno je unijeti email adresu učenika"),
    parentEmail: yup
      .string()
      .email("Unesite ispravnu email adresu")
      .required("Potrebno je unijeti email adresu roditelja"),
  });

  const handleSubmit = async (applicationFormData: any) => {
    const requestData = {
      first_name: applicationFormData.firstName,
      last_name: applicationFormData.lastName,
      school: applicationFormData.school,
      phone_number: applicationFormData.phoneNumber,
      parent_phone_number: applicationFormData.parentPhoneNumber,
      email: applicationFormData.email,
      parent_email: applicationFormData.parentEmail,
      remark: applicationFormData.remark,
      subjects: [CROATIAN_ESSAY_ID],
      currency: "EUR",
    };

    try {
      await api.post("/applications/", requestData);
      navigate("/prijava-uspjesno-poslana");
    } catch (error) {
      console.log(error);
      toast.error("Greška prilikom slanja prijave");
    }
  };

  return (
    <div className="essay-application-page">
      <h1>Prijava mature - hrvatski esej</h1>
      <hr />

      <div className="essay-application-info" role="status">
        <p>
          <strong>Trajanje:</strong> 18. travnja 2026. - 23. svibnja 2026.
        </p>
        <p>
          <strong>Vrijeme:</strong> subota od 15 do 17 sati
        </p>
        <p className="mb-0">
          <strong>Cijena:</strong> 130 €
        </p>
      </div>

      <h4>Obrazac prijave na pripreme za pisanje eseja</h4>

      <Formik
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{
          firstName: "",
          lastName: "",
          school: "",
          phoneNumber: "",
          parentPhoneNumber: "",
          email: "",
          parentEmail: "",
          remark: "",
        }}
      >
        {({
          handleSubmit,
          handleChange,
          isSubmitting,
          handleBlur,
          values,
          touched,
          errors,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Row className="mb-3">
              <FormGroup as={Col} controlId="formName">
                <Form.Label>Ime učenika</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite ime učenika"
                  name="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.firstName && !!errors.firstName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </FormGroup>
              <FormGroup as={Col} controlId="formLastName">
                <Form.Label>Prezime učenika</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite prezime učenika"
                  name="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.lastName && !!errors.lastName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastName}
                </Form.Control.Feedback>
              </FormGroup>
            </Row>

            <Row className="mb-3">
              <FormGroup controlId="formSchool">
                <Form.Label>Naziv škole</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite naziv škole"
                  name="school"
                  value={values.school}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.school && !!errors.school}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.school}
                </Form.Control.Feedback>
              </FormGroup>
            </Row>

            <Row className="mb-3">
              <FormGroup as={Col} controlId="formPhoneNumber">
                <Form.Label>Kontakt broj učenika</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite kontakt broj učenika"
                  name="phoneNumber"
                  value={values.phoneNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.phoneNumber && !!errors.phoneNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phoneNumber}
                </Form.Control.Feedback>
              </FormGroup>
              <FormGroup as={Col} controlId="formParentPhoneNumber">
                <Form.Label>Kontakt broj roditelja</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite kontakt broj roditelja"
                  name="parentPhoneNumber"
                  value={values.parentPhoneNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={
                    touched.parentPhoneNumber && !!errors.parentPhoneNumber
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {errors.parentPhoneNumber}
                </Form.Control.Feedback>
              </FormGroup>
            </Row>

            <Row className="mb-3">
              <FormGroup as={Col} controlId="formEmail">
                <Form.Label>Email učenika</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite email adresu učenika"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.email && !!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </FormGroup>
              <FormGroup as={Col} controlId="formParentEmail">
                <Form.Label>Email roditelja</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite email adresu roditelja"
                  name="parentEmail"
                  value={values.parentEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.parentEmail && !!errors.parentEmail}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.parentEmail}
                </Form.Control.Feedback>
              </FormGroup>
            </Row>

            <Row className="mb-3">
              <FormGroup controlId="formRemark">
                <Form.Label>Napomena</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Napomena (npr. ukoliko želite rad isključivo vikendom ili nešto što smatrate bitnim)"
                  name="remark"
                  value={values.remark}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormGroup>
            </Row>

            <Button
              variant="primary"
              type="submit"
              className="float-end"
              disabled={isSubmitting}
            >
              {isSubmitting && <Spinner size="sm" className="ml-1" />}
              Prijavi se
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default MaturaCroatianEssayApplicationPage;
